import { useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import type { LessonBlock, ModuleDetail, TrainingLesson } from '@xsc/shared';
import { api, ApiError } from '../lib/api-client.js';
import { Card, CardBody, CardHeader, CardTitle } from '../components/ui/card.js';
import { ScormBlockView } from '../components/training/scorm-block-view.js';
import { Button } from '../components/ui/button.js';
import { Badge } from '../components/ui/badge.js';

/**
 * Module detail / lesson viewer (Task #17).
 *
 * Left rail: lesson list with completion checkmarks.
 * Right pane: typed JSONB content blocks rendered as semantic HTML.
 * Bottom CTA: "Mark complete" (POSTs progress, advances to next lesson),
 * or "Start quiz" when every lesson is done.
 */
type LessonWithDone = TrainingLesson & { is_completed: boolean };

function CalloutBlock({
  title,
  items,
  variant,
  html,
}: {
  title: string;
  items: string[];
  variant?: 'info' | 'warning' | 'success' | 'danger';
  html?: boolean;
}): JSX.Element {
  const tone = {
    info: 'border-blue-300 bg-blue-50 dark:bg-blue-900/20 dark:border-blue-700',
    warning: 'border-amber-300 bg-amber-50 dark:bg-amber-900/20 dark:border-amber-700',
    success: 'border-green-300 bg-green-50 dark:bg-green-900/20 dark:border-green-700',
    danger: 'border-red-300 bg-red-50 dark:bg-red-900/20 dark:border-red-700',
  }[variant ?? 'info'];
  return (
    <aside className={`rounded border-l-4 px-4 py-3 my-3 ${tone}`}>
      <div className="font-semibold text-sm mb-2">{title}</div>
      <div className="space-y-1 text-sm [&_p]:my-1 [&_a]:underline [&_a]:text-accent
                       [&_ol]:list-decimal [&_ul]:list-disc [&_ol]:ml-5 [&_ul]:ml-5">
        {items.map((line, i) =>
          line === '' ? (
            <div key={i} className="h-2" />
          ) : html ? (
            <div key={i} dangerouslySetInnerHTML={{ __html: line }} />
          ) : (
            <p key={i}>{line}</p>
          ),
        )}
      </div>
    </aside>
  );
}

function VideoBlock({
  block,
  onProgress,
}: {
  block: Extract<LessonBlock, { type: 'video' }>;
  onProgress?: (furthestSec: number, durationSec: number) => void;
}): JSX.Element {
  const furthestRef = useRef(0);
  const lastReportRef = useRef(0);
  if (block.provider === 'youtube' || block.provider === 'vimeo') {
    // Embed-only — the video gate cannot be enforced for third-party
    // iframes (no postMessage handshake here yet). Authors should prefer
    // `mp4` for gated content.
    const src =
      block.provider === 'youtube'
        ? `https://www.youtube-nocookie.com/embed/${encodeURIComponent(extractYouTubeId(block.url))}`
        : `https://player.vimeo.com/video/${encodeURIComponent(extractVimeoId(block.url))}`;
    return (
      <figure className="my-3">
        {block.title && <figcaption className="font-semibold mb-2">{block.title}</figcaption>}
        <div className="aspect-video w-full rounded overflow-hidden border border-border">
          <iframe
            src={src}
            title={block.title ?? 'Video'}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            className="w-full h-full"
          />
        </div>
        <p className="text-xs text-ink-muted mt-1">
          Embedded video — watch progress is not auto-tracked. Please watch the entire video
          before marking the lesson complete.
        </p>
      </figure>
    );
  }
  return (
    <figure className="my-3">
      {block.title && <figcaption className="font-semibold mb-2">{block.title}</figcaption>}
      <video
        src={block.url}
        controls
        preload="metadata"
        className="w-full rounded border border-border"
        onTimeUpdate={(e) => {
          const v = e.currentTarget;
          const cur = Math.floor(v.currentTime);
          if (cur > furthestRef.current) furthestRef.current = cur;
          const now = Date.now();
          if (now - lastReportRef.current < 5_000) return;
          if (!Number.isFinite(v.duration) || v.duration === 0) return;
          lastReportRef.current = now;
          onProgress?.(furthestRef.current, Math.floor(v.duration));
        }}
        onEnded={(e) => {
          const v = e.currentTarget;
          if (!Number.isFinite(v.duration) || v.duration === 0) return;
          onProgress?.(Math.floor(v.duration), Math.floor(v.duration));
        }}
      />
    </figure>
  );
}

function extractYouTubeId(url: string): string {
  const m = url.match(/(?:v=|youtu\.be\/|embed\/)([\w-]{6,})/);
  return m?.[1] ?? url;
}
function extractVimeoId(url: string): string {
  const m = url.match(/vimeo\.com\/(?:video\/)?(\d+)/);
  return m?.[1] ?? url;
}

function renderBlock(
  block: LessonBlock,
  key: number,
  onVideoProgress?: (furthestSec: number, durationSec: number) => void,
  onScormCompleted?: () => void,
): JSX.Element {
  switch (block.type) {
    case 'video':
      return <VideoBlock key={key} block={block} onProgress={onVideoProgress} />;
    case 'scorm':
      return (
        <ScormBlockView
          key={key}
          packageId={block.package_id}
          title={block.title}
          onCompleted={onScormCompleted}
        />
      );
    case 'heading':
      return block.level === 3 ? (
        <h3 key={key} className="text-base font-semibold mt-4 mb-2">{block.text}</h3>
      ) : (
        <h2 key={key} className="text-lg font-semibold mt-5 mb-2">{block.text}</h2>
      );
    case 'paragraph':
      // Task #18 admin authoring uses Quill, which emits HTML. Plain seed
      // strings (no tags) render the same way through dangerouslySetInnerHTML.
      return (
        <div
          key={key}
          className="my-2 leading-relaxed [&_p]:my-2 [&_a]:underline [&_a]:text-accent
                     [&_ol]:list-decimal [&_ul]:list-disc [&_ol]:ml-6 [&_ul]:ml-6"
          dangerouslySetInnerHTML={{ __html: block.text }}
        />
      );
    case 'callout':
      return <CalloutBlock key={key} title={block.title} items={block.items} variant={block.variant} html />;
    case 'list': {
      const items = block.items.map((it, i) => <li key={i} className="my-1">{it}</li>);
      return block.ordered ? (
        <ol key={key} className="list-decimal ml-6 my-2">{items}</ol>
      ) : (
        <ul key={key} className="list-disc ml-6 my-2">{items}</ul>
      );
    }
    case 'table':
      return (
        <div key={key} className="my-3 overflow-x-auto">
          <table className="w-full text-sm border border-border">
            <thead className="bg-surface-alt">
              <tr>
                {block.headers.map((h, i) => (
                  <th key={i} className="border border-border px-2 py-1 text-left font-semibold">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {block.rows.map((row, i) => (
                <tr key={i}>
                  {row.map((cell, j) => (
                    <td key={j} className="border border-border px-2 py-1 align-top">{cell}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    case 'exercise':
      return (
        <aside key={key} className="rounded border border-border bg-surface-alt p-4 my-3">
          <div className="text-xs uppercase tracking-wide text-ink-muted mb-1">Exercise</div>
          <div className="font-semibold mb-2">{block.title}</div>
          <p className="text-sm whitespace-pre-line">{block.instructions}</p>
          {block.prompt && (
            <p className="text-sm mt-2 italic text-ink-muted">{block.prompt}</p>
          )}
        </aside>
      );
    case 'divider':
      return <hr key={key} className="my-4 border-border" />;
  }
}

export function TrainingModulePage(): JSX.Element {
  const { moduleId = '' } = useParams<{ moduleId: string }>();
  const [searchParams] = useSearchParams();
  const renewed = searchParams.get('renewed') === '1';
  const qc = useQueryClient();
  const [activeIdx, setActiveIdx] = useState(0);

  const detailQ = useQuery({
    queryKey: ['training', 'module', moduleId],
    queryFn: () => api.get<ModuleDetail>(`/v1/training/modules/${encodeURIComponent(moduleId)}`),
    enabled: Boolean(moduleId),
  });

  const [gateError, setGateError] = useState<string | null>(null);
  const markComplete = useMutation({
    mutationFn: (lessonId: string) =>
      api.post(`/v1/training/modules/${encodeURIComponent(moduleId)}/progress`, {
        lesson_id: lessonId,
      }),
    onSuccess: async () => {
      setGateError(null);
      await qc.invalidateQueries({ queryKey: ['training', 'module', moduleId] });
      await qc.invalidateQueries({ queryKey: ['training', 'modules'] });
    },
    onError: (err) => {
      if (err instanceof ApiError && err.status === 409) {
        const body = err.body as {
          error?: string;
          pct?: number;
          score?: number | null;
          required?: number | null;
        } | null;
        if (body?.error === 'scorm_score_required') {
          const score = body.score !== null && body.score !== undefined
            ? `${Math.round(body.score)}%`
            : 'below the threshold';
          setGateError(
            `Your score (${score}) is below the required passing score of ${body.required ?? '—'}%. Retry the attempt in the lesson to try again.`,
          );
        } else if (body?.error === 'scorm_completion_required') {
          setGateError('The interactive package must report passed or completed before this lesson can be marked complete.');
        } else {
          const pct = body?.pct ?? 0;
          setGateError(`Please watch at least 90% of the video first (currently ${Math.round(pct * 100)}%).`);
        }
      } else {
        setGateError('Could not save progress. Please try again.');
      }
    },
  });

  const reportWatch = (lessonId: string, furthestSec: number, durationSec: number) => {
    api
      .post(
        `/v1/training/modules/${encodeURIComponent(moduleId)}/lessons/${encodeURIComponent(lessonId)}/watch-progress`,
        { furthest_sec: furthestSec, duration_sec: durationSec },
      )
      .catch(() => {
        /* best-effort */
      });
  };

  if (detailQ.isLoading) return <p className="text-ink-muted">Loading…</p>;
  if (detailQ.isError || !detailQ.data) {
    return (
      <div className="space-y-3">
        <p className="text-danger">This module could not be loaded.</p>
        <Link to="/training"><Button variant="secondary">Back to training</Button></Link>
      </div>
    );
  }

  const { module: mod, lessons } = detailQ.data;
  const lessonList = lessons as LessonWithDone[];
  if (lessonList.length === 0) {
    return (
      <div className="space-y-3">
        <h1 className="text-2xl font-semibold">{mod.title}</h1>
        <p className="text-ink-muted">This module has no lessons yet.</p>
        <Link to="/training"><Button variant="secondary">Back to training</Button></Link>
      </div>
    );
  }

  const safeIdx = Math.min(activeIdx, lessonList.length - 1);
  const active = lessonList[safeIdx]!;
  const allDone = lessonList.every((l) => l.is_completed);

  return (
    <div className="space-y-4">
      {renewed && (
        <aside
          role="status"
          className="rounded border-l-4 px-4 py-3 border-amber-300 bg-amber-50 dark:bg-amber-900/20 dark:border-amber-700"
        >
          <div className="font-semibold text-sm mb-1">
            Your certificate is expiring soon
          </div>
          <p className="text-sm">
            Re-complete this module and pass the quiz to refresh your
            certification for another full term. Your existing certificate
            stays valid until its expiry date.
          </p>
        </aside>
      )}
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div>
          <Link to="/training" className="text-sm text-ink-muted hover:text-ink">
            ← All training
          </Link>
          <h1 className="text-2xl font-semibold mt-1">{mod.title}</h1>
          {mod.description && (
            <p className="text-sm text-ink-muted">{mod.description}</p>
          )}
        </div>
        <div className="flex items-center gap-2">
          {mod.is_required && <Badge tone="warning">Required</Badge>}
          <Badge tone="neutral">Pass ≥ {mod.passing_score}%</Badge>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-[16rem_1fr] gap-4">
        <Card>
          <CardHeader><CardTitle>Lessons</CardTitle></CardHeader>
          <CardBody className="p-0">
            <ol className="divide-y divide-border">
              {lessonList.map((l, i) => (
                <li key={l.id}>
                  <button
                    type="button"
                    onClick={() => setActiveIdx(i)}
                    className={`w-full text-left px-3 py-2 text-sm flex items-start gap-2 hover:bg-surface ${i === safeIdx ? 'bg-surface' : ''}`}
                  >
                    <span aria-hidden className="mt-0.5 text-xs">
                      {l.is_completed ? '✓' : `${i + 1}.`}
                    </span>
                    <span className="flex-1">
                      <span className="block">{l.title}</span>
                      <span className="block text-xs text-ink-muted">
                        {l.estimated_minutes} min · {l.lesson_type}
                      </span>
                    </span>
                  </button>
                </li>
              ))}
            </ol>
          </CardBody>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>{active.title}</CardTitle>
            {active.is_completed && <Badge tone="success">Completed</Badge>}
          </CardHeader>
          <CardBody>
            <article className="prose-sm max-w-none">
              {active.content.map((b, i) =>
                renderBlock(
                  b,
                  i,
                  (furthest, dur) => reportWatch(active.id, furthest, dur),
                  () => {
                    // SCORM bridge already inserted training_progress on the
                    // server; invalidate caches so the checkmark + button
                    // refresh.
                    void qc.invalidateQueries({ queryKey: ['training', 'module', moduleId] });
                    void qc.invalidateQueries({ queryKey: ['training', 'modules'] });
                  },
                ),
              )}
            </article>
            {gateError && (
              <p className="text-sm text-warning mt-3">{gateError}</p>
            )}
            <div className="mt-6 flex items-center justify-between gap-2 flex-wrap">
              <Button
                variant="secondary"
                disabled={safeIdx === 0}
                onClick={() => setActiveIdx((i) => Math.max(0, i - 1))}
              >
                Previous
              </Button>
              <div className="flex items-center gap-2">
                {!active.is_completed && !active.content.some((b) => b.type === 'scorm') && (
                  <Button
                    disabled={markComplete.isPending}
                    onClick={() =>
                      markComplete.mutate(active.id, {
                        onSuccess: () => {
                          if (safeIdx < lessonList.length - 1) {
                            setActiveIdx(safeIdx + 1);
                          }
                        },
                      })
                    }
                  >
                    {markComplete.isPending ? 'Saving…' : 'Mark complete'}
                  </Button>
                )}
                {!active.is_completed && active.content.some((b) => b.type === 'scorm') && (
                  <span className="text-xs text-ink-muted italic">
                    Completion is recorded automatically when the SCORM package
                    reports passed or completed.
                  </span>
                )}
                {safeIdx < lessonList.length - 1 ? (
                  <Button
                    variant="secondary"
                    onClick={() => setActiveIdx((i) => Math.min(lessonList.length - 1, i + 1))}
                  >
                    Next
                  </Button>
                ) : (
                  allDone && (
                    <Link to={`/training/${moduleId}/quiz`}>
                      <Button>Start quiz</Button>
                    </Link>
                  )
                )}
              </div>
            </div>
          </CardBody>
        </Card>
      </div>
    </div>
  );
}
